import { DEFAULT_FORCE, type ForceParams, type LayoutKind } from './layout/types.ts'
import { isHexColor } from './colors.ts'

// ============================================================================
// Workbench settings — the small bag of view / layout knobs that get tweaked
// often during a session (which layout, what's repulsing how hard, which
// display toggles are on). Unlike the grammar and host graph (the *authored*
// content, which lives in undo/redo), these are workspace preferences: they
// persist to localStorage so they stick across reloads, and serialise to a
// portable JSON file so a tuned setup can be saved and shared.
// ============================================================================

export type { ForceParams } from './layout/types.ts'

/** The full, serialisable set of frequently-adjusted workbench preferences. */
export interface WorkbenchSettings {
  /** Active layout engine. */
  layout: LayoutKind
  /** Force-simulation tuning (repulsion / link distance / collision). */
  force: ForceParams
  /** Draw node / edge labels. */
  showLabels: boolean
  /** Highlight where the selected rule currently matches. */
  preview: boolean
  /** Re-settle the layout when a rule rewires edges (the "reflow" toggle). */
  reflow: boolean
  /** Drop hidden nodes/edges from the layout so the rest re-spreads. */
  respread: boolean
  /**
   * Node / edge labels switched off in the display filter — the same set the
   * filter popover and the legend chips toggle. Stored as label-name arrays so
   * they persist and serialise; an entry for a label not currently in the graph
   * is harmless (it simply matches nothing).
   */
  hiddenNodes: string[]
  hiddenEdges: string[]
  /**
   * Per-label colour overrides (label → `#rrggbb`). Empty by default: a label's
   * colour is the hash of its name, which can collide for distinct labels. An
   * entry here pins a label to a chosen colour so the user can tell them apart.
   */
  labelColors: Record<string, string>
}

/** Allowed layout kinds, for validating imported / stored values. */
const LAYOUT_KINDS: readonly LayoutKind[] = ['force', 'dagre', 'elk']

/**
 * Inclusive bounds for the force knobs. The sliders, `clampSettings`, and
 * importers all clamp to these, so no value path (UI, JSON, localStorage) can
 * push the simulation somewhere unstable.
 */
export const FORCE_BOUNDS: Record<keyof ForceParams, { min: number; max: number }> = {
  charge: { min: -3000, max: 0 }, // negative = repulsion; bigger magnitude spreads dense graphs
  linkDistance: { min: 5, max: 600 },
  collideRadius: { min: 0, max: 120 },
}

/** Factory for the stock settings (deep copy so callers can mutate freely). */
export function defaultSettings(): WorkbenchSettings {
  return {
    layout: 'force',
    force: { ...DEFAULT_FORCE },
    showLabels: true,
    preview: true,
    reflow: true,
    respread: false,
    hiddenNodes: [],
    hiddenEdges: [],
    labelColors: {},
  }
}

/** Keep only the (de-duplicated) string entries of an untrusted label list (from
 *  imported / hand-edited JSON). */
function sanitizeLabelList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const out = new Set<string>()
  for (const x of raw) if (typeof x === 'string') out.add(x)
  return [...out]
}

/** Keep only well-formed `label → hex` entries from an untrusted overrides map
 *  (from imported / hand-edited JSON or a colour picker). */
function sanitizeLabelColors(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  if (raw && typeof raw === 'object') {
    for (const [label, color] of Object.entries(raw as Record<string, unknown>)) {
      if (label && isHexColor(color)) out[label] = color
    }
  }
  return out
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

/** Coerce one possibly-bad force value to a finite number within its bound. */
function clampForce(key: keyof ForceParams, v: unknown): number {
  const b = FORCE_BOUNDS[key]
  const n = typeof v === 'number' && Number.isFinite(v) ? v : DEFAULT_FORCE[key]
  return clamp(n, b.min, b.max)
}

/** Normalise a fully-formed settings object: clamp the numbers, validate the
 *  layout, coerce the flags. Returns a fresh object. */
export function clampSettings(s: WorkbenchSettings): WorkbenchSettings {
  return {
    layout: LAYOUT_KINDS.includes(s.layout) ? s.layout : 'force',
    force: {
      charge: clampForce('charge', s.force?.charge),
      linkDistance: clampForce('linkDistance', s.force?.linkDistance),
      collideRadius: clampForce('collideRadius', s.force?.collideRadius),
    },
    showLabels: !!s.showLabels,
    preview: !!s.preview,
    reflow: !!s.reflow,
    respread: !!s.respread,
    hiddenNodes: sanitizeLabelList(s.hiddenNodes),
    hiddenEdges: sanitizeLabelList(s.hiddenEdges),
    labelColors: sanitizeLabelColors(s.labelColors),
  }
}

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] }

/**
 * Layer a (deep) partial over the defaults and clamp the result. Tolerant of
 * missing / unknown fields, so it safely upgrades older or hand-written JSON.
 */
export function mergeSettings(patch: DeepPartial<WorkbenchSettings> | null | undefined): WorkbenchSettings {
  const base = defaultSettings()
  if (!patch || typeof patch !== 'object') return base
  return clampSettings({
    ...base,
    ...patch,
    force: { ...base.force, ...(patch.force ?? {}) },
  } as WorkbenchSettings)
}

/** The on-disk wrapper: a small marker + version around the settings payload,
 *  so a settings file is distinguishable from a grammar / graph export. */
interface SettingsFile {
  kind: 'graph-grammar-settings'
  version: 1
  settings: WorkbenchSettings
}

/** Serialise settings to a portable JSON document (with a kind/version marker). */
export function serializeSettings(s: WorkbenchSettings): string {
  const doc: SettingsFile = { kind: 'graph-grammar-settings', version: 1, settings: clampSettings(s) }
  return JSON.stringify(doc, null, 2)
}

export type ParseSettingsResult = { ok: true; settings: WorkbenchSettings } | { ok: false; error: string }

/**
 * Parse a settings JSON document. Accepts either the wrapped form produced by
 * {@link serializeSettings} or a bare settings object (so partial / hand-edited
 * JSON loads too). Always returns clamped, defaults-filled settings on success.
 */
export function parseSettings(text: string): ParseSettingsResult {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return { ok: false, error: 'not valid JSON' }
  }
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'expected a JSON object' }
  // Unwrap the {kind, version, settings} envelope when present; otherwise treat
  // the object itself as the settings.
  const obj = raw as Record<string, unknown>
  const payload =
    obj.kind === 'graph-grammar-settings' && obj.settings && typeof obj.settings === 'object' ? obj.settings : obj
  return { ok: true, settings: mergeSettings(payload as DeepPartial<WorkbenchSettings>) }
}
